import { openDB, type IDBPDatabase } from 'idb'
import type { FileRecord, SessionState } from './types'

const DB_NAME = 'nitido-db'
const DB_VERSION = 1
const FILES_STORE = 'files'
const SESSION_STORE = 'session'

let dbPromise: Promise<IDBPDatabase> | null = null

function getDB(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(database) {
        if (!database.objectStoreNames.contains(FILES_STORE)) {
          const store = database.createObjectStore(FILES_STORE, { keyPath: 'id' })
          store.createIndex('status', 'status')
          store.createIndex('category', 'category')
          store.createIndex('createdAt', 'createdAt')
        }
        if (!database.objectStoreNames.contains(SESSION_STORE)) {
          database.createObjectStore(SESSION_STORE, { keyPath: 'id' })
        }
      },
    }).catch((err) => {
      // Auto-reset on failure so subsequent attempts can retry opening
      dbPromise = null
      throw err
    })
  }
  return dbPromise
}

/**
 * Closes the active IndexedDB connection and resets the singleton promise.
 */
export function closeDB(): void {
  if (dbPromise) {
    dbPromise.then((database) => database.close()).catch(() => {})
    dbPromise = null
  }
}

export async function upsertFileRecord(record: FileRecord): Promise<void> {
  const database = await getDB()
  await database.put(FILES_STORE, { ...record, updatedAt: Date.now() })
}

export async function getFileRecord(id: string): Promise<FileRecord | undefined> {
  const database = await getDB()
  return database.get(FILES_STORE, id)
}

export async function getAllFileRecords(): Promise<FileRecord[]> {
  const database = await getDB()
  return database.getAll(FILES_STORE)
}

export async function deleteFileRecord(id: string): Promise<void> {
  const database = await getDB()
  await database.delete(FILES_STORE, id)
}

export async function clearAllFileRecords(): Promise<void> {
  const database = await getDB()
  await database.clear(FILES_STORE)
}

export async function saveSession(session: SessionState): Promise<void> {
  const database = await getDB()
  await database.put(SESSION_STORE, { ...session, lastActivityAt: Date.now() })
}

export async function loadSession(): Promise<SessionState | undefined> {
  const database = await getDB()
  const all: SessionState[] = await database.getAll(SESSION_STORE)
  // Only one session at a time — return the most recent
  return all.sort((a, b) => b.lastActivityAt - a.lastActivityAt)[0]
}

export async function clearSession(): Promise<void> {
  const database = await getDB()
  await database.clear(SESSION_STORE)
}
